import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Button, Modal } from 'react-bootstrap';
import { Send, X, MessageCircle, Trash2, Plus, List, Smile, Paperclip, Users } from 'lucide-react';
import signalRService from '../Services/signalRService';
import useChats from '../hooks/DataFetching/useChats';
import { useDispatch, useSelector } from 'react-redux';
import { setSelectedChatTopic } from '../redux/actions/topicActions';
import { addPost, deletePost, setError } from '../redux/actions/chatActions';
import '../styles/components/Chat.css';
import CreateChatForm from './CreateChatForm';
import { useTranslation } from '../i18n';
import { Link, useLocation } from 'react-router-dom';

const GUIDE_TOPIC_ID = 'socratic-guide';
const EMOJI_OPTIONS = [
  '😂',
  '❤️',
  '👍',
  '😭',
  '🙏',
  '😍',
  '😊',
  '🔥',
  '🤣',
  '😎',
  '😢',
  '🎉',
  '💯',
  '🤔',
  '😅',
  '🙌',
  '👏',
  '😬',
  '😌',
  '🥰',
  '✅',
  '✨',
  '🤩',
  '🤷',
];

export function Chat({
  isDarkMode = false,
  inline = false,
  initiallyOpen = false,
  guideOnly = false,
  showFloatingTrigger = true,
  onClose,
  onGuideMessageSent,
  onGuideInviteAccepted,
}) {
  const { user, isAuthenticated, isLoading, getAccessTokenSilently, loginWithRedirect } = useAuth0();
  const { t } = useTranslation();
  const guideIntro = useMemo(
    () =>
      isAuthenticated
        ? t(
            'guide.initialAuthenticated',
            "Hi! I’m the Kingdom Guide. Ask me anything about the Kingdom, and I’ll help you get the most out of your membership."
          )
        : t(
            'guide.initial',
            "Hi! I’m the Kingdom Guide. Ask me anything—and I’ll help you decide if joining is right for you. When you're ready, I’ll invite you to create your profile."
          ),
    [isAuthenticated, t]
  );
  const [newMessage, setNewMessage] = useState("");
  const [localError, setLocalError] = useState(null);
  const [isOpen, setIsOpen] = useState(inline || initiallyOpen);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSignalRConnected, setIsSignalRConnected] = useState(false);
  const [showCreateChatForm, setShowCreateChatForm] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [messageToDelete, setMessageToDelete] = useState(null);
  const [participants, setParticipants] = useState({});
  const [guideMessages, setGuideMessages] = useState(() => [
    {
      role: 'assistant',
      content: guideIntro,
    },
  ]);
  const [guideInput, setGuideInput] = useState('');
  const [guideLoading, setGuideLoading] = useState(false);
  const [guideInviteVisible, setGuideInviteVisible] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showGuideEmojiPicker, setShowGuideEmojiPicker] = useState(false);
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedImageName, setSelectedImageName] = useState('');
  const dispatch = useDispatch();
  const { posts, topics, loading, fetchPosts, fetchTopics, sendMessage } = useChats();
  const selectedTopic = useSelector((state) => state.topic?.selectedChatTopic);
  const location = useLocation();
  const messageRefs = useRef({});
  const [pendingChatTarget, setPendingChatTarget] = useState(null);
  const chatMessagesRef = useRef(null);
  const guideMessagesRef = useRef(null);
  const messageInputRef = useRef(null);
  const guideInputRef = useRef(null);
  const fileInputRef = useRef(null);
  const apiUrl = process.env.REACT_APP_API_URL;

  const guideTopic = useMemo(
    () => ({ id: GUIDE_TOPIC_ID, title: t('guide.title', 'Kingdom Guide'), isGuide: true }),
    [t]
  );

  const insertEmoji = useCallback((emoji, value, setValue, inputRef) => {
    const input = inputRef.current;
    if (input && typeof input.selectionStart === 'number') {
      const start = input.selectionStart;
      const end = input.selectionEnd;
      const nextValue = `${value.slice(0, start)}${emoji}${value.slice(end)}`;
      setValue(nextValue);
      requestAnimationFrame(() => {
        input.focus();
        const cursor = start + emoji.length;
        input.setSelectionRange(cursor, cursor);
      });
    } else {
      setValue(`${value}${emoji}`);
    }
  }, []);

  const availableTopics = useMemo(() => {
    if (guideOnly) {
      return [guideTopic];
    }

    const otherTopics = Array.isArray(topics) ? topics : [];
    if (!isAuthenticated) {
      return [guideTopic];
    }
    return [guideTopic, ...otherTopics];
  }, [guideTopic, topics, isAuthenticated, guideOnly]);

  const isGuideSelected = selectedTopic?.id === GUIDE_TOPIC_ID;

  const registerMessageRef = useCallback((postId, node) => {
    if (node) {
      messageRefs.current[postId] = node;
    } else {
      delete messageRefs.current[postId];
    }
  }, []);

  const highlightMessage = useCallback((postId) => {
    const target = messageRefs.current[postId];
    if (target) {
      target.classList.add('notification-highlight');
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setTimeout(() => target.classList.remove('notification-highlight'), 2000);
    }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const view = params.get('view');
    const topicIdParam = params.get('topicId');
    const postIdParam = params.get('postId');

    if (view === 'chat' && topicIdParam) {
      setIsOpen(true);
      setPendingChatTarget({
        topicId: Number(topicIdParam),
        postId: postIdParam ? Number(postIdParam) : null,
      });
    }
  }, [location.search]);

  useEffect(() => {
    if (!selectedTopic) {
      dispatch(setSelectedChatTopic(guideTopic));
      return;
    }

    if (selectedTopic.id === GUIDE_TOPIC_ID && selectedTopic.title !== guideTopic.title) {
      dispatch(setSelectedChatTopic(guideTopic));
      return;
    }

    if (!isAuthenticated && selectedTopic.id !== GUIDE_TOPIC_ID) {
      dispatch(setSelectedChatTopic(guideTopic));
    }
  }, [selectedTopic, dispatch, guideTopic, isAuthenticated]);

  const handleReceiveMessage = useCallback((newMessage) => {
    dispatch(addPost(newMessage));

    if (selectedTopic && selectedTopic.id === newMessage.topicId && newMessage.topicId !== GUIDE_TOPIC_ID) {
      fetchPosts(selectedTopic.id);
    }
  }, [dispatch, selectedTopic, fetchPosts]);

  const handleNewChat = useCallback((chatId, chatTitle) => {
    if (guideOnly) {
      return;
    }
    fetchTopics();
  }, [fetchTopics, guideOnly]);

  const handleDeletedMessage = useCallback((deletedMessageId, topicId) => {
    const currentTopicId = selectedTopic?.id;
    console.log(`Message deleted: ${deletedMessageId} in topic: ${topicId}`);
    dispatch(deletePost(deletedMessageId));
    if (currentTopicId && currentTopicId === topicId && currentTopicId !== GUIDE_TOPIC_ID) {
      fetchPosts(topicId);
    }
  }, [dispatch, selectedTopic, fetchPosts]);

  useEffect(() => {
    let isMounted = true;
    const unsubscribeState = signalRService.onStateChange((state) => {
      if (isMounted) setIsSignalRConnected(state === 'connected');
    });
    const initializeSignalR = async () => {
      if (isAuthenticated) {
        try {
          await signalRService.start(() => getAccessTokenSilently({
            authorizationParams: { audience: process.env.REACT_APP_API_AUDIENCE },
          }));
          if (isMounted) {
            signalRService.onChatMessage(handleReceiveMessage);
            signalRService.onNewChat(handleNewChat);
            signalRService.onDeleteChatMessage(handleDeletedMessage);
          }
        } catch (error) {
          if (isMounted) {
            setIsSignalRConnected(false);
          }
        }
      } else await signalRService.stop();
    };
  
    initializeSignalR();

    return () => {
      isMounted = false;
      unsubscribeState();
      signalRService.offChatMessage(handleReceiveMessage);
      signalRService.offNewChat(handleNewChat);
      signalRService.offDeleteChatMessage(handleDeletedMessage);
    };
  }, [isAuthenticated, isLoading, getAccessTokenSilently, handleReceiveMessage, handleNewChat, handleDeletedMessage]);

  useEffect(() => {
    if (guideOnly) {
      return;
    }
    if (isAuthenticated && !isLoading) {
      fetchTopics();
    }
  }, [fetchTopics, isAuthenticated, isLoading, guideOnly]);

  useEffect(() => {
    if (!isGuideSelected && chatMessagesRef.current) {
      chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
    }
  }, [posts, isGuideSelected]);

  useEffect(() => {
    if (isGuideSelected && guideMessagesRef.current) {
      guideMessagesRef.current.scrollTop = guideMessagesRef.current.scrollHeight;
    }
  }, [guideMessages, guideLoading, guideInviteVisible, isGuideSelected]);

  useEffect(() => {
    setGuideMessages((prev) => {
      if (prev.length === 1 && prev[0].role === 'assistant') {
        return [{ role: 'assistant', content: guideIntro }];
      }
      return prev;
    });
  }, [guideIntro]);

  const fetchChatParticipants = useCallback(async (topicId) => {
    if (!isAuthenticated || topicId === GUIDE_TOPIC_ID) {
      return;
    }
    try {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: process.env.REACT_APP_API_AUDIENCE,
        },
      });

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/chats/GetChatParticipants/${topicId}`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        }
      });

      if (response.ok) {
        const participantIds = await response.json();
        const participantNames = await Promise.all(participantIds.map(async (userId) => {
          const nameResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/Users/GetUserHandle/${userId}`, {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            }
          });
          if (nameResponse.ok) {
            return { userId, name: await nameResponse.text() };
          }
          return { userId, name: 'Unknown User' };
        }));

        const participantMap = participantNames.reduce((acc, { userId, name }) => {
          acc[userId] = name;
          return acc;
        }, {});

        setParticipants(participantMap);
      } else {
        console.error('Failed to fetch chat participants');
      }
    } catch (error) {
      console.error('Error fetching chat participants:', error);
    }
  }, [getAccessTokenSilently, isAuthenticated]);

  const handleTopicClick = useCallback((topic) => {
    if (localError) {
      setLocalError(null);
    }
    if (topic?.id === GUIDE_TOPIC_ID || topic?.isGuide) {
      dispatch(setSelectedChatTopic(guideTopic));
      setIsSidebarOpen(false);
      return;
    }

    dispatch(setSelectedChatTopic(topic));
    fetchPosts(topic.id);
    fetchChatParticipants(topic.id);
    setIsSidebarOpen(false);
  }, [dispatch, fetchPosts, fetchChatParticipants, guideTopic, localError]);

  useEffect(() => {
    if (!pendingChatTarget?.topicId) return;

    const targetTopic = availableTopics.find((topic) => topic.id === pendingChatTarget.topicId);
    if (targetTopic) {
      handleTopicClick(targetTopic);
    } else {
      fetchTopics();
    }
  }, [availableTopics, fetchTopics, handleTopicClick, pendingChatTarget]);

  useEffect(() => {
    if (!pendingChatTarget?.postId) return;
    if (selectedTopic?.id !== pendingChatTarget.topicId) return;
    if (!posts || posts.length === 0) return;

    const target = messageRefs.current[pendingChatTarget.postId];
    if (!target) return;

    highlightMessage(pendingChatTarget.postId);
    setPendingChatTarget((prev) => (prev ? { ...prev, postId: null } : null));
  }, [highlightMessage, pendingChatTarget, posts, selectedTopic]);

  const sendGuideMessage = useCallback(async () => {
    const trimmedMessage = guideInput.trim();
    if (!trimmedMessage || guideLoading) {
      return;
    }

    const updatedMessages = [...guideMessages, { role: 'user', content: trimmedMessage }];
    setGuideMessages(updatedMessages);
    setGuideInput('');
    setGuideLoading(true);

    if (typeof onGuideMessageSent === 'function') {
      const userMessageCount = updatedMessages.filter((message) => message.role === 'user').length;
      try {
        onGuideMessageSent({ message: trimmedMessage, messageCount: userMessageCount });
      } catch (error) {
        // swallow analytics errors to avoid interrupting the chat flow
      }
    }

    try {
      const headers = { 'Content-Type': 'application/json' };
      if (isAuthenticated) {
        try {
          const token = await getAccessTokenSilently({
            authorizationParams: {
              audience: process.env.REACT_APP_API_AUDIENCE,
            },
          });
          headers.Authorization = `Bearer ${token}`;
        } catch (tokenError) {
          console.warn('Token retrieval failed', tokenError);
        }
      }

      const response = await fetch(`${apiUrl}/api/socraticguide/chat`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ messages: updatedMessages }),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.message || 'Chat request failed');
      }

      if (data?.nextAction === 'invite_profile') {
        setGuideInviteVisible(true);
      }

      setGuideMessages([...updatedMessages, { role: 'assistant', content: data.response }]);
    } catch (err) {
      console.error('Chat request failed', err);
      setGuideMessages((prev) => [
        ...prev,
        { role: 'assistant', content: 'Sorry—something went wrong. Please try again.' },
      ]);
    } finally {
      setGuideLoading(false);
    }
  }, [
    guideInput,
    guideMessages,
    isAuthenticated,
    getAccessTokenSilently,
    apiUrl,
    guideLoading,
    onGuideMessageSent,
  ]);

  const handleSendMessage = useCallback(async () => {
    if (isGuideSelected) {
      await sendGuideMessage();
      return;
    }

    if (
      isSendingMessage ||
      (!newMessage.trim() && !selectedImage) ||
      !selectedTopic ||
      selectedTopic.id === GUIDE_TOPIC_ID
    )
      return;

    try {
      setIsSendingMessage(true);
      await sendMessage(selectedTopic.id, newMessage, null, null, selectedImage);
      setNewMessage('');
      setSelectedImage(null);
      setSelectedImageName('');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      setLocalError(t('chat.sendFailed', 'Failed to send message.'));
    } finally {
      setIsSendingMessage(false);
    }
  }, [
    isGuideSelected,
    sendGuideMessage,
    newMessage,
    selectedTopic,
    sendMessage,
    isSendingMessage,
    selectedImage,
    t,
  ]);

  const handleImageSelect = useCallback(
    (event) => {
      const file = event.target.files?.[0];
      if (!file) {
        setSelectedImage(null);
        setSelectedImageName('');
        return;
      }

      const maxSizeBytes = 10 * 1024 * 1024;
      if (file.size > maxSizeBytes) {
        setLocalError(t('chat.imageTooLarge', 'Images must be 10 MB or smaller before upload.'));
        setSelectedImage(null);
        setSelectedImageName('');
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }

      setLocalError(null);
      setSelectedImage(file);
      setSelectedImageName(file.name);
    },
    [t]
  );

  const clearSelectedImage = useCallback(() => {
    setSelectedImage(null);
    setSelectedImageName('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  useEffect(() => {
    clearSelectedImage();
  }, [selectedTopic?.id, clearSelectedImage]);

  const handleDeleteMessage = useCallback(async (messageId) => {
    setMessageToDelete(messageId);
    setShowDeleteConfirmation(true);
  }, []);

  const confirmDeleteMessage = useCallback(async () => {
    if (!messageToDelete) return;
      try {
          const token = await getAccessTokenSilently({
            authorizationParams: {
                audience: process.env.REACT_APP_API_AUDIENCE,
            },
        });

        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/chats/${messageToDelete}`, {
            method: 'DELETE',
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });

        if (response.ok) {
            dispatch(deletePost(messageToDelete));
        } else {
            dispatch(setError("Failed to delete message."));
        }
      } catch (error) {
          console.error("An error occurred while deleting the message", error);
          dispatch(setError("An error occurred while deleting the message."));
      } finally {
          setShowDeleteConfirmation(false);
          setMessageToDelete(null);
      }
  }, [messageToDelete, dispatch, getAccessTokenSilently]);


  const handleCreateChat = useCallback((newTopic) => {
    if (guideOnly) {
      return;
    }
    fetchTopics();
    handleTopicClick(newTopic);
    setShowCreateChatForm(false);
  }, [fetchTopics, handleTopicClick, guideOnly]);

  const normalizeId = useCallback((id) => id.replace(/[-|]/g, ''), []);

  if (isLoading && isAuthenticated) {
    return <div>Loading...</div>;
  }

  const conversationTitle = isGuideSelected
    ? guideTopic.title
    : selectedTopic?.title || t('chat.conversations', 'Conversations');

  const guideMessageCount = Math.max(0, guideMessages.length - 1);

  const rootClasses = ['socratic-chat'];
  if (isDarkMode) rootClasses.push('dark-mode');
  if (inline) rootClasses.push('inline');

  return (
    <div className={rootClasses.join(' ')}>
      {!inline && !isOpen && showFloatingTrigger && (
        <button
          type="button"
          aria-label={t('chat.open', 'Open chat')}
          className="socratic-chat__trigger"
          onClick={() => {
            setIsOpen(true);
            setIsSidebarOpen(false);
          }}
        >
          <MessageCircle size={28} />
        </button>
      )}
      {(inline || isOpen) && (
        <div className="socratic-chat__panel">
          <div className="socratic-chat__header">
            <div className="socratic-chat__title">
              <button
                type="button"
                className="socratic-chat__icon-button socratic-chat__mobile-toggle"
                aria-label={t('chat.openConversations', 'Open conversations list')}
                onClick={() => setIsSidebarOpen(true)}
              >
                <List size={18} />
              </button>
              <span>{conversationTitle}</span>
            </div>
            <div className="socratic-chat__actions">
              <button
                type="button"
                className="socratic-chat__icon-button"
                aria-label={t('chat.toggleConversations', 'Toggle conversations')}
                onClick={() => setIsSidebarOpen((prev) => !prev)}
              >
                <MessageCircle size={18} />
              </button>
              {isAuthenticated && !guideOnly && (
                <button
                  type="button"
                  className="socratic-chat__icon-button"
                  aria-label={t('chat.newConversation', 'Start a new conversation')}
                  onClick={() => setShowCreateChatForm(true)}
                >
                  <Plus size={18} />
                </button>
              )}
              {!inline && (
                <button
                  type="button"
                  className="socratic-chat__icon-button"
                  aria-label={t('chat.close', 'Close chat')}
                  onClick={() => {
                    setIsOpen(false);
                    setIsSidebarOpen(false);
                    if (typeof onClose === 'function') {
                      onClose();
                    }
                  }}
                >
                  <X size={20} />
                </button>
              )}
            </div>
          </div>

          {isAuthenticated && !guideOnly && !isSignalRConnected && (
            <div className="socratic-chat__error" role="status">
              {t('chat.reconnecting', 'Realtime updates are reconnecting. You can keep reading this conversation.')}
            </div>
          )}

          <div className="socratic-chat__body">
            <aside className={`socratic-chat__sidebar ${isSidebarOpen ? 'open' : ''}`}>
              <div className="socratic-chat__sidebar-header">
                <span>{t('chat.conversations', 'Conversations')}</span>
                <button
                  type="button"
                  aria-label={t('chat.closeConversations', 'Close conversations list')}
                  className="socratic-chat__icon-button"
                  onClick={() => setIsSidebarOpen(false)}
                >
                  <X size={16} />
                </button>
              </div>
              <div className="socratic-chat__sidebar-list">
                {loading && isAuthenticated ? (
                  <div className="socratic-chat__empty">{t('chat.loadingTopics', 'Loading conversations…')}</div>
                ) : localError ? (
                  <div className="socratic-chat__error">{localError}</div>
                ) : (
                  availableTopics.map((topic) => {
                    const badgeCount = topic.id === GUIDE_TOPIC_ID
                      ? guideMessageCount
                      : topic.messageCount || 0;
                    const isActive = selectedTopic?.id === topic.id || (isGuideSelected && topic.id === GUIDE_TOPIC_ID);
                    return (
                      <button
                        key={topic.id}
                        type="button"
                        className={`socratic-chat__topic ${isActive ? 'active' : ''}`}
                        onClick={() => handleTopicClick(topic)}
                      >
                        <span className="socratic-chat__topic-title">{topic.title}</span>
                        <span className="socratic-chat__topic-count">{badgeCount}</span>
                      </button>
                    );
                  })
                )}
              </div>
              {isAuthenticated && !guideOnly && (
                <div className="socratic-chat__sidebar-actions">
                  <Link
                    to="/user-search"
                    className="socratic-chat__find-friends"
                    onClick={() => {
                      setIsOpen(false);
                      setIsSidebarOpen(false);
                      if (typeof onClose === 'function') {
                        onClose();
                      }
                    }}
                  >
                    <Users size={16} /> {t('chat.findFriends', 'Find friends')}
                  </Link>
                  <button
                    type="button"
                    className="socratic-chat__new-topic"
                    onClick={() => {
                      setShowCreateChatForm(true);
                      setIsSidebarOpen(false);
                    }}
                  >
                    <Plus size={16} /> {t('chat.startConversation', 'Start a conversation')}
                  </button>
                </div>
              )}
            </aside>

            <section className="socratic-chat__conversation">
              <div
                className="socratic-chat__messages"
                ref={isGuideSelected ? guideMessagesRef : chatMessagesRef}
              >
                {isGuideSelected ? (
                  <>
                    {guideMessages.map((message, index) => (
                      <div
                        key={`${message.role}-${index}`}
                        className={`guide-message ${message.role === 'user' ? 'user' : 'assistant'}`}
                      >
                        <div className="guide-message__bubble">{message.content}</div>
                      </div>
                    ))}
                    {guideLoading && (
                      <div className="guide-message guide-message--status">
                        {t('guide.thinking', 'Thinking…')}
                      </div>
                    )}
                    {guideInviteVisible && (
                      <div className="guide-invite">
                        <p>{t('guide.inviteCopy', 'The Guide is ready to help you begin your profile setup.')}</p>
                        {!isAuthenticated && (
                          <button
                            type="button"
                            className="guide-invite__button"
                            onClick={() => {
                              if (typeof onGuideInviteAccepted === 'function') {
                                try {
                                  onGuideInviteAccepted();
                                } catch (error) {
                                  // ignore analytics errors
                                }
                              }
                              loginWithRedirect({
                                authorizationParams: {
                                  screen_hint: 'signup',
                                  redirect_uri: `${window.location.origin}/profile-setup`,
                                },
                              });
                            }}
                          >
                            {t('landing.ctaPrimary', 'Continue to Sign Up')}
                          </button>
                        )}
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    {loading ? (
                      <div className="socratic-chat__empty">{t('chat.loadingMessages', 'Loading messages…')}</div>
                    ) : localError ? (
                      <div className="socratic-chat__error">{localError}</div>
                    ) : selectedTopic ? (
                      posts.length > 0 ? (
                        posts.map((post) => (
                          <div
                            key={post.id}
                            ref={(node) => registerMessageRef(post.id, node)}
                            className="chat-message-wrapper"
                          >
                            <ChatMessage
                              post={post}
                              isDarkMode={isDarkMode}
                              normalizeId={normalizeId}
                              userSub={user?.sub || ''}
                              handleDeleteMessage={handleDeleteMessage}
                              userName={participants[post.userId] || 'Unknown User'}
                            />
                          </div>
                        ))
                      ) : (
                        <div className="socratic-chat__empty">{t('chat.noMessages', 'No messages in this conversation yet.')}</div>
                      )
                    ) : (
                      <div className="socratic-chat__empty">{t('chat.selectConversation', 'Select a conversation to start chatting.')}</div>
                    )}
                  </>
                )}
              </div>

              <div className="socratic-chat__footer">
                {isGuideSelected ? (
                  <div className="guide-input">
                    <textarea
                      ref={guideInputRef}
                      value={guideInput}
                      onChange={(e) => setGuideInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          if (e.repeat) {
                            return;
                          }
                          sendGuideMessage();
                        }
                      }}
                      rows={2}
                      placeholder={t('guide.placeholder', 'Ask a question… (Enter to send)')}
                    />
                    <div className="guide-input__actions">
                      <div className="emoji-picker">
                        <button
                          type="button"
                          className="emoji-picker__toggle"
                          onClick={() => setShowGuideEmojiPicker((prev) => !prev)}
                          aria-label={t('chat.addEmoji', 'Add emoji')}
                        >
                          <Smile size={18} />
                        </button>
                        {showGuideEmojiPicker && (
                          <div className="emoji-picker__panel" role="listbox" aria-label={t('chat.emojiList', 'Emoji list')}>
                            {EMOJI_OPTIONS.map((emoji) => (
                              <button
                                type="button"
                                key={`guide-emoji-${emoji}`}
                                className="emoji-picker__option"
                                onClick={() => {
                                  insertEmoji(emoji, guideInput, setGuideInput, guideInputRef);
                                  setShowGuideEmojiPicker(false);
                                }}
                              >
                                {emoji}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      <button
                        type="button"
                        className="guide-send-button"
                        onClick={sendGuideMessage}
                        disabled={guideLoading || !guideInput.trim()}
                      >
                        {t('guide.send', 'Send')}
                      </button>
                    </div>
                    <p className="guide-input__note">
                      {isAuthenticated
                        ? t(
                            'guide.noteAuthenticated',
                            'You can ask about the Kingdom, your profile, or how to participate.'
                          )
                        : t('guide.note', 'Joining is available only through this Guide conversation.')}
                    </p>
                  </div>
                ) : (
                  selectedTopic && selectedTopic.id !== GUIDE_TOPIC_ID && (
                    <div className="conversation-input">
                      <input
                        type="file"
                        accept="image/*"
                        ref={fileInputRef}
                        style={{ display: 'none' }}
                        onChange={handleImageSelect}
                      />
                      {selectedImageName && (
                        <div className="attached-image-chip">
                          <span className="attached-image-name" title={selectedImageName}>
                            {selectedImageName}
                          </span>
                          <span className="attached-image-status" role="status" aria-live="polite">
                            {t('chat.imageAttached', 'Image attached')}
                          </span>
                          <button
                            type="button"
                            className="clear-image-button"
                            onClick={clearSelectedImage}
                            aria-label={t('chat.removeImage', 'Remove attached image')}
                          >
                            <X size={14} />
                          </button>
                        </div>
                      )}
                      <input
                        type="text"
                        ref={messageInputRef}
                        value={newMessage}
                        onChange={(e) => {
                          setNewMessage(e.target.value);
                          if (localError) {
                            setLocalError(null);
                          }
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            if (e.repeat) {
                              return;
                            }
                            handleSendMessage();
                          }
                        }}
                        placeholder={t('chat.messagePlaceholder', 'Type a message…')}
                      />
                      <div className="emoji-picker">
                        <button
                          type="button"
                          className="emoji-picker__toggle"
                          onClick={() => setShowEmojiPicker((prev) => !prev)}
                          aria-label={t('chat.addEmoji', 'Add emoji')}
                        >
                          <Smile size={18} />
                        </button>
                        {showEmojiPicker && (
                          <div className="emoji-picker__panel" role="listbox" aria-label={t('chat.emojiList', 'Emoji list')}>
                            {EMOJI_OPTIONS.map((emoji) => (
                              <button
                                type="button"
                                key={`chat-emoji-${emoji}`}
                                className="emoji-picker__option"
                                onClick={() => {
                                  insertEmoji(emoji, newMessage, setNewMessage, messageInputRef);
                                  setShowEmojiPicker(false);
                                }}
                              >
                                {emoji}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      <button
                        type="button"
                        className="conversation-attach-button"
                        onClick={() => fileInputRef.current?.click()}
                        aria-label={t('chat.attachImage', 'Attach an image')}
                        title={t('chat.attachImage', 'Attach an image')}
                      >
                        <Paperclip size={18} />
                      </button>
                      <button
                        type="button"
                        className="conversation-send-button"
                        onClick={handleSendMessage}
                        disabled={!newMessage.trim() && !selectedImage}
                        aria-label={t('chat.sendMessage', 'Send message')}
                        title={t('chat.sendMessage', 'Send message')}
                      >
                        <Send size={18} aria-hidden="true" />
                      </button>
                    </div>
                  )
                )}
              </div>
            </section>
          </div>
        </div>
      )}
      {!guideOnly && (
        <CreateChatForm
          show={showCreateChatForm}
          handleClose={() => setShowCreateChatForm(false)}
          onCreateChat={handleCreateChat}
        />
      )}
      <Modal show={showDeleteConfirmation} onHide={() => setShowDeleteConfirmation(false)} className="tyk-modal" centered>
        <Modal.Header closeButton closeVariant="white">
          <Modal.Title>{t('chat.confirmDelete', 'Confirm Delete')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="tyk-panel">
            {t('chat.confirmDeleteBody', 'Are you sure you want to delete this message?')}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteConfirmation(false)}>
            {t('chat.cancel', 'Cancel')}
          </Button>
          <Button variant="danger" onClick={confirmDeleteMessage}>
            {t('chat.delete', 'Delete')}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

const ChatMessage = React.memo(({ post, isDarkMode, normalizeId, userSub, handleDeleteMessage, userName }) => {
  const isCurrentUserMessage = post.userId === normalizeId(userSub);

  return (
    <div className={`chat-message ${isCurrentUserMessage ? 'sent' : 'received'} ${isDarkMode ? 'dark-message' : ''}`}>
      <div className="message-content">
        <strong>{userName}</strong>
        {post.body && <p>{post.body}</p>}
        {post.imageUrl && (
          <div className="message-image-wrapper">
            <img src={post.imageUrl} alt="Uploaded attachment" className="message-image" loading="lazy" />
          </div>
        )}
      </div>
      {isCurrentUserMessage && (
        <Button 
          variant="link" 
          className="delete-button"
          onClick={() => handleDeleteMessage(post.id)}
        >
          <Trash2 size={16} />
        </Button>
      )}
    </div>
  );
});

ChatMessage.displayName = 'ChatMessage';

export default Chat;
